import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate, useSearchParams, useLocation, Link } from "react-router-dom";
import { Icon } from '@iconify/react';
import MasterLayout from "../masterLayout/MasterLayout";
import Breadcrumb from "../components/Breadcrumb";
import AddVenueLayer from "../components/AddVenueLayer";
import VenueGalleryManager from "../components/VenueGalleryManager";
import VenueMenuManager from "../components/VenueMenuManager";
import VenueFeaturesManager from "../components/VenueFeaturesManager";
import VenueBlogsManager from "../components/VenueBlogsManager";
import VenueVouchersManager from "../components/VenueVouchersManager";
import VenueFeaturedManager from "../components/VenueFeaturedManager";
import VenuePreviewModal from "../components/VenuePreviewModal";
import { useAuth } from "../context/AuthContext";
import config from "../config";

const EditVenuePage = () => {
    const { id } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { auth, user } = useAuth();

    const activeTab = searchParams.get('tab') || 'details';
    const tabsBarRef = useRef(null);
    const [venue, setVenue] = useState(null);
    const [loadingVenue, setLoadingVenue] = useState(true);
    const galleryVisited = useRef(false);
    if (activeTab === 'gallery' && !loadingVenue) galleryVisited.current = true;
    const [showPreview, setShowPreview] = useState(false);

    const fetchVenueHeader = useCallback(async () => {
        if (!id) return;
        try {
            setLoadingVenue(true);
            const res = await auth.fetch(`${config.API_BASE_URL}/api/v2/venues/${id}`);
            if (res.ok) {
                const data = await res.json();
                setVenue(data);
            }
        } catch (e) {
            console.error('Error loading venue header info:', e);
        } finally {
            setLoadingVenue(false);
        }
    }, [auth, id]);

    useEffect(() => {
        fetchVenueHeader();
    }, [fetchVenueHeader]);

    // On phones the tab row scrolls sideways, so keep the selected tab in view
    useEffect(() => {
        const bar = tabsBarRef.current;
        const tab = bar?.querySelector('.venue-tab-btn.active');
        if (!tab || bar.scrollWidth <= bar.clientWidth) return;
        const barRect = bar.getBoundingClientRect();
        const tabRect = tab.getBoundingClientRect();
        bar.scrollTo({
            left: bar.scrollLeft + tabRect.left - barRect.left - (bar.clientWidth - tabRect.width) / 2,
            behavior: 'smooth'
        });
    }, [activeTab, loadingVenue]);

    const handleTabChange = (tabKey) => {
        setSearchParams({ tab: tabKey });
    };

    const handleSuccess = () => {
        fetchVenueHeader();
        const returnPath = location.state?.returnTo || (user?.role === 'admin' ? '/venue-verification' : '/my-venues');
        navigate(returnPath, { state: { openVenueId: id } });
    };

    const tabs = [
        { id: 'details', label: 'Listing Details', icon: 'solar:pen-bold' },
        { id: 'gallery', label: 'Gallery Media', icon: 'solar:gallery-bold', count: venue?.gallery?.length },
        { id: 'menu', label: 'Menu Items', icon: 'solar:chef-hat-bold' },
        { id: 'features', label: 'Features & Offers', icon: 'solar:star-bold', count: venue?.features?.length },
        { id: 'blogs', label: 'Blog Posts & Events', icon: 'solar:document-text-bold', count: venue?.blogs?.length },
        { id: 'vouchers', label: 'Vouchers & Deals', icon: 'solar:ticket-sale-bold' },
        ...(user?.role === 'admin' ? [{ id: 'featured', label: 'Featured', icon: 'solar:crown-bold' }] : [])
    ];

    const getStatusBadge = (status) => {
        const variants = {
            approved: { cls: 'hs-badge-success', icon: 'solar:check-circle-bold', label: 'Approved' },
            pending: { cls: 'hs-badge-warning', icon: 'solar:clock-circle-bold', label: 'Pending Review' },
            rejected: { cls: 'hs-badge-danger', icon: 'solar:close-circle-bold', label: 'Rejected' },
            suspended: { cls: 'hs-badge-neutral', icon: 'solar:danger-triangle-bold', label: 'Suspended' }
        };
        const s = variants[status] || variants.pending;
        return (
            <span className={`hs-badge ${s.cls} d-inline-flex align-items-center gap-1.5`}>
                <Icon icon={s.icon} className="text-xs" />
                <span>{s.label}</span>
            </span>
        );
    };

    const venueThumbnail = venue?.thumbnail_url || (venue?.thumbnail ? (venue.thumbnail.startsWith('http') ? venue.thumbnail : `${config.API_BASE_URL}/${venue.thumbnail}`) : null);
    const venueLogo = venue?.logo_url || (venue?.logo ? (venue.logo.startsWith('http') ? venue.logo : `${config.API_BASE_URL}/${venue.logo}`) : null);

    return (
        <MasterLayout>
            <Breadcrumb title={`Edit Venue: ${venue?.title || venue?.business_name || 'Listing'}`} />

            <div className="d-flex flex-column gap-20">
                {/* 1. Venue Profile Header Card */}
                {loadingVenue && !venue ? (
                    <div className="hs-card mb-0 shadow-sm border radius-16 bg-white p-24 text-center">
                        <div className="spinner-border spinner-border-sm text-primary me-2" role="status" />
                        <span className="text-secondary-light text-sm">Loading venue profile...</span>
                    </div>
                ) : venue && (
                    <div className="hs-card mb-0 shadow-sm border radius-16 bg-white overflow-hidden">
                        <div className="p-20 px-24">
                            <div className="row align-items-center gy-3">
                                {/* Logo / Avatar */}
                                <div className="col-auto">
                                    {venueLogo || venueThumbnail ? (
                                        <img
                                            src={venueLogo || venueThumbnail}
                                            alt={venue.title}
                                            className="w-72-px h-72-px rounded-16 object-fit-cover shadow-2xs border"
                                            onError={(e) => {
                                                e.currentTarget.onerror = null;
                                                e.currentTarget.src = '/assets/images/placeholder.png';
                                            }}
                                        />
                                    ) : (
                                        <div className="w-72-px h-72-px rounded-16 bg-primary-50 text-primary-600 border d-flex align-items-center justify-content-center">
                                            <Icon icon="solar:shop-2-bold" className="text-3xl" />
                                        </div>
                                    )}
                                </div>

                                {/* Title & Info */}
                                <div className="col">
                                    <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
                                        <h4 className="mb-0 fw-bold text-dark" style={{ letterSpacing: '-0.3px' }}>
                                            {venue.title || venue.business_name}
                                        </h4>
                                        {getStatusBadge(venue.verification_status || 'pending')}
                                    </div>
                                    <p className="text-secondary-light text-xs mb-2 d-flex align-items-center gap-1.5 flex-wrap">
                                        <Icon icon="solar:map-point-bold" className="text-primary-600 text-sm" />
                                        <span>
                                            {venue.street && `${venue.street}, `}
                                            {venue.city && `${venue.city}, `}
                                            {venue.country || venue.address || 'Dubai, UAE'}
                                        </span>
                                        {venue.primary_category_name && (
                                            <>
                                                <span className="text-neutral-300">•</span>
                                                <span className="badge rounded-pill bg-neutral-100 text-secondary-light border px-2 py-0.5">
                                                    {venue.primary_category_name}
                                                </span>
                                            </>
                                        )}
                                        <span className="text-neutral-300">•</span>
                                        <span>ID #{venue.id}</span>
                                    </p>

                                    {/* Stat chips */}
                                    <div className="d-flex gap-2 flex-wrap">
                                        <span className="hs-chip" title="Likes from mobile app users">
                                            <Icon icon="solar:heart-bold" className="text-danger-600" />
                                            <span>{venue.likes_count || 0} {venue.likes_count === 1 ? 'Like' : 'Likes'}</span>
                                        </span>
                                        <button
                                            type="button"
                                            onClick={() => handleTabChange('gallery')}
                                            className="hs-chip text-decoration-none border-0 cursor-pointer d-inline-flex align-items-center justify-content-center gap-1.5"
                                            title="View Gallery Media"
                                        >
                                            <Icon icon="solar:gallery-bold" className="text-primary-600" />
                                            <span>{venue.gallery?.length || 0} Media Photos</span>
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => handleTabChange('menu')}
                                            className="hs-chip text-decoration-none border-0 cursor-pointer d-inline-flex align-items-center justify-content-center gap-1.5"
                                            title="View Menu Items"
                                        >
                                            <Icon icon="solar:chef-hat-bold" className="text-warning-600" />
                                            <span>Menu Items</span>
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => handleTabChange('features')}
                                            className="hs-chip text-decoration-none border-0 cursor-pointer d-inline-flex align-items-center justify-content-center gap-1.5"
                                            title="View Features & Amenities"
                                        >
                                            <Icon icon="solar:star-bold" className="text-success-600" />
                                            <span>{venue.features?.length || 0} Amenities & Offers</span>
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => handleTabChange('blogs')}
                                            className="hs-chip text-decoration-none border-0 cursor-pointer d-inline-flex align-items-center justify-content-center gap-1.5"
                                            title="View Blog Posts"
                                        >
                                            <Icon icon="solar:document-text-bold" className="text-info-600" />
                                            <span>{venue.blogs?.length || 0} Feed Posts</span>
                                        </button>
                                    </div>
                                </div>

                                {/* Actions */}
                                <div className="col-auto">
                                    <div className="d-flex align-items-center gap-2 flex-wrap">
                                        <button
                                            type="button"
                                            className="hs-btn-primary hs-btn-sm d-inline-flex align-items-center gap-1.5"
                                            onClick={() => setShowPreview(true)}
                                            title="Preview Customer Experience"
                                        >
                                            <Icon icon="solar:eye-bold" />
                                            <span>Live Preview</span>
                                        </button>
                                        <Link
                                            to={location.state?.returnTo || (user?.role === 'admin' ? "/venue-verification" : "/my-venues")}
                                            state={{ openVenueId: id }}
                                            className="hs-btn-secondary hs-btn-sm text-decoration-none d-inline-flex align-items-center gap-1.5"
                                        >
                                            <Icon icon="solar:arrow-left-linear" className="text-base" />
                                            <span>{(location.state?.returnTo === '/venue-verification' || user?.role === 'admin') ? 'Back to Verification' : 'Back to Venues'}</span>
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* 2. Navigation Tabs Toolbar */}
                <div className="hs-card mb-0 p-2 bg-white border radius-12 shadow-none">
                    <style>{`
                        .venue-tabs-bar {
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            width: 100%;
                            gap: 6px;
                        }
                        .venue-tab-btn {
                            flex: 1 1 0;
                            min-width: 0;
                            display: inline-flex;
                            align-items: center;
                            justify-content: center;
                            text-align: center;
                            border: 1px solid transparent;
                            background: transparent;
                            color: #334155 !important;
                            padding: 10px 14px;
                            border-radius: 9px;
                            font-size: 13px;
                            font-weight: 600;
                            white-space: nowrap;
                        }
                        .venue-tab-btn:hover {
                            color: #0a2540 !important;
                            background: #e0f2fe !important;
                            border-color: #bae6fd !important;
                        }
                        .venue-tab-btn.active {
                            background: #ffffff !important;
                            border-color: #0789d5 !important;
                            color: #0789d5 !important;
                        }
                        @media (max-width: 991px) {
                            .venue-tabs-bar {
                                overflow-x: auto;
                                justify-content: flex-start;
                            }
                            .venue-tab-btn {
                                flex: 0 0 auto;
                            }
                            .hs-nav-pills .venue-tab-btn {
                                padding: 8px 12px;
                            }
                        }
                    `}</style>
                    <div className="hs-nav-pills venue-tabs-bar" ref={tabsBarRef}>
                        {tabs.map((tab) => (
                            <button
                                key={tab.id}
                                className={`hs-nav-link venue-tab-btn ${
                                    activeTab === tab.id ? 'active shadow-xs' : ''
                                }`}
                                onClick={() => handleTabChange(tab.id)}
                                type="button"
                            >
                                <Icon icon={tab.icon} className="text-base flex-shrink-0 text-primary-600" style={{ color: '#0789d5' }} />
                                <span>{tab.label}</span>
                                {tab.count !== undefined && tab.count > 0 && (
                                    <span
                                        className="badge rounded-pill px-2 py-0.5 ms-1"
                                        style={{
                                            fontSize: '10px',
                                            backgroundColor: activeTab === tab.id ? 'rgba(7, 137, 213, 0.15)' : 'rgba(0,0,0,0.06)',
                                            color: activeTab === tab.id ? '#0789d5' : '#475569'
                                        }}
                                    >
                                        {tab.count}
                                    </span>
                                )}
                            </button>
                        ))}
                    </div>
                </div>

                {/* 3. Tab Content */}
                <div>
                    {activeTab === 'details' && (
                        <AddVenueLayer
                            venueId={id}
                            onSuccess={handleSuccess}
                            onNavigateTab={handleTabChange}
                        />
                    )}

                    {/* Opens from the venue data already loaded, then stays mounted */}
                    {galleryVisited.current && (
                        <div className={activeTab === 'gallery' ? '' : 'd-none'}>
                            <VenueGalleryManager
                                key={id}
                                venueId={id}
                                initialImages={venue?.gallery}
                                initialLimit={venue?.limits?.galleryLimit}
                            />
                        </div>
                    )}

                    {activeTab === 'menu' && (
                        <VenueMenuManager venueId={id} />
                    )}

                    {activeTab === 'features' && (
                        <VenueFeaturesManager venueId={id} />
                    )}

                    {activeTab === 'blogs' && (
                        <VenueBlogsManager
                            venueId={id}
                            isOwner={user && venue && (user.id === venue.owner_id || user.role === 'admin')}
                        />
                    )}

                    {activeTab === 'vouchers' && (
                        <VenueVouchersManager
                            venueId={id}
                            initialVouchers={venue?.vouchers}
                            onUpdated={fetchVenueHeader}
                        />
                    )}

                    {activeTab === 'featured' && user?.role === 'admin' && (
                        <VenueFeaturedManager
                            venueId={id}
                            venue={venue}
                            onUpdated={fetchVenueHeader}
                        />
                    )}
                </div>
            </div>

            {/* Live Customer Preview Modal */}
            <VenuePreviewModal
                show={showPreview}
                onHide={() => setShowPreview(false)}
                venueId={id}
            />
        </MasterLayout>
    );
};

export default EditVenuePage;
